import { useCallback, useEffect, useState, useMemo } from "react";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { getRajasthanSurplusDeficitMetrics } from "../Utility/RestApi";
import { convertToString } from "../../../extensions/date";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { SurplusDeficitData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh"];

const GEN_BY = [
  {
    label: "Cumulative",
    keys: ["cumulative"],
  },
  {
    label: "Comparative",
    keys: ["comparative"],
  },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

interface RajasthanForecastSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  genGroupBy: any;
  onGenGroupByChange: (index: number) => void;
  metrics: SurplusDeficitData | null;
}

export default function RajasthanForecastSummaryAnalysis({
  onXAxisModeChange,
  selectedDiscom,
  onDiscomChange,
  genGroupBy,
  onGenGroupByChange,
  metrics,
}: RajasthanForecastSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  // State management
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [loading, setLoading] = useState(true);

  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  // Fetch summary data from API - only calls getRajasthanSurplusDeficitMetrics
  const fetchSummaryData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;

      const startDateStr = convertToString(dates.startDate);
      const endDateStr = convertToString(dates.endDate);

      // Only call the surplus/deficit metrics API
      const data = await getRajasthanSurplusDeficitMetrics(
        startDateStr,
        endDateStr,
      );

      setSummaryData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Process and filter data
  useEffect(() => {
    if (summaryData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const conversionFactor = selectedUnit === "MWh" ? 0.25 : 1;
    let filteredData = summaryData;

    // Filter by selected blocks
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.slot),
      );
    }

    if (filteredData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Process surplus/deficit data
    const processedData = filteredData.map((item) => {
      const baseData = {
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time: `${item.slot}`,
      };

      // Drawal metrics
      const drawalData = {
        drawal_min: item.min_drawal * conversionFactor,
        drawal_max: item.max_drawal * conversionFactor,
        drawal_avg: item.avg_drawal * conversionFactor,
        drawal_mode: item.mode_drawal * conversionFactor,
        drawal_date_of_min: item.date_of_min_drawal,
        drawal_date_of_max: item.date_of_max_drawal,
      };

      // Total Gen + Proc metrics
      const genProcData = {
        total_gen_proc_min: item.min_total_gen_proc * conversionFactor,
        total_gen_proc_max: item.max_total_gen_proc * conversionFactor,
        total_gen_proc_avg: item.avg_total_gen_proc * conversionFactor,
        total_gen_proc_mode: item.mode_total_gen_proc * conversionFactor,
        total_gen_proc_date_of_min: item.date_of_min_total_gen_proc,
        total_gen_proc_date_of_max: item.date_of_max_total_gen_proc,
      };

      // Surplus/Deficit metrics
      const surplusDeficitData = {
        surplus_deficit_min: item.min_surplus_deficit * conversionFactor,
        surplus_deficit_max: item.max_surplus_deficit * conversionFactor,
        surplus_deficit_avg: item.avg_surplus_deficit * conversionFactor,
        surplus_deficit_mode: item.mode_surplus_deficit * conversionFactor,
        surplus_deficit_date_of_min: item.date_of_min_surplus_deficit,
        surplus_deficit_date_of_max: item.date_of_max_surplus_deficit,
      };

      // Surplus/Deficit percentage metrics
      const surplusDeficitPctData = {
        surplus_deficit_pct_min: item.min_surplus_deficit_pct,
        surplus_deficit_pct_max: item.max_surplus_deficit_pct,
        surplus_deficit_pct_avg: item.avg_surplus_deficit_pct,
        surplus_deficit_pct_mode: item.mode_surplus_deficit_pct,
        surplus_deficit_pct_date_of_min: item.date_of_min_surplus_deficit_pct,
        surplus_deficit_pct_date_of_max: item.date_of_max_surplus_deficit_pct,
      };

      return {
        ...baseData,
        ...drawalData,
        ...genProcData,
        ...surplusDeficitData,
        ...surplusDeficitPctData,
      };
    });

    processedData.sort((a, b) => a.slot - b.slot);

    setChartData(processedData);
    setTableData(processedData);

    // Set chart keys based on what metrics we want to display
    setChartKeys(["drawal", "total_gen_proc", "surplus_deficit"]);
  }, [summaryData, selectedUnit, selectedBlock]);

  // Generate legends for chart
  const legends = useMemo(() => {
    const legendsArray: any[] = [];

    const metricTypes = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];

    const dataCategories = [
      { key: "drawal", label: "Drawal" },
      { key: "total_gen_proc", label: "Total Gen. + Proc." },
      { key: "surplus_deficit", label: "Surplus/Deficit" },
    ];

    // ✅ Generate category legends
    dataCategories.forEach((category) => {
      metricTypes.forEach((metric) => {
        legendsArray.push({
          dataKey: `${category.key}_${metric.field}`,
          name: `${category.label} ${metric.displayName}`,

          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,

          stackId:
            selectedChartType.stackId !== undefined ? category.key : undefined,

          dateKey: metric.hasDate
            ? `${category.key}_date_of_${metric.field}`
            : undefined,
        });
      });
    });

    // ✅ Add Surplus/Deficit Percentage metrics
    const pctMetricTypes = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];

    pctMetricTypes.forEach((metric) => {
      legendsArray.push({
        dataKey: `surplus_deficit_pct_${metric.field}`,
        name: `Surplus/Deficit % ${metric.displayName}`,

        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,

        yAxisId: "right", // ✅ Right Y-axis for percentage
        unit: "%",

        stackId:
          selectedChartType.stackId !== undefined
            ? "surplus_deficit_pct"
            : undefined,
        dateKey: metric.hasDate
          ? `surplus_deficit_pct_date_of_${metric.field}`
          : undefined,
      });
    });

    return legendsArray;
  }, [selectedChartType]);

  // Custom tooltip formatter
  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      const matchingLegend = legends.find((leg) => leg.name === seriesName);
      const dateKey = matchingLegend?.dateKey;

      if (dateKey && dataPoint?.[dateKey]) {
        return `${seriesName} (${dataPoint[dateKey]})`;
      }
      return seriesName;
    },
    [legends],
  );

  const getExtraKeys = () => {
    return ["slot_number", "Date"];
  };

  const getExtraLabels = () => {
    return ["Block", "Date"];
  };
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Forecast Surplus Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || {},
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => {
              onGenGroupByChange(index);
            }}
          />

          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={(item) => {
              onDiscomChange(item);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Surplus/Deficit"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
            ]}
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Total Generation"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.unit ??
                  "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.unit ??
                  "",
              },
            ]}
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title="Load"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.load_metrics?.["Max Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Max Load (MW)"]?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.load_metrics?.["Min Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Min Load (MW)"]?.unit ?? "",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading">
              Surplus/Deficit Summary ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {chartData.length === 0 && !loading && (
            <div
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              No data available for the selected filters. Please adjust your
              selection.
            </div>
          )}

          {dataTab.label === "Chart" && chartData.length > 0 && (
            <ReMixChart
              key={`${selectedChartType.value}-${selectedUnit}`}
              data={chartData}
              showBrush={undefined}
              isTimeSlot={true}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              secondYAxisLabel="%"
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey="slot"
              secondXDataKey={undefined}
              resolveDisplayName={resolveTooltipName}
            />
          )}

          {dataTab.label === "Summary" && chartData.length > 0 && (
            <LegendSummaryTable {...tablePar} />
          )}
          {dataTab.label === "Table" && chartData.length > 0 && (
            <ChartTable {...tablePar} />
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>Forecast of Total Drawal and Generation + Procurement</p>
              }
              text="1. Surplus/Deficit = Total Gen. + Proc. - Total Drawal
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
